import { BarcodeOutlined, MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, App, Button, Flex, Form, Input, InputNumber, Modal, Radio, Segmented, Select, Space, Table, Tag, Typography } from "antd";
import { useDeferredValue, useState } from "react";
import { useNavigate } from "react-router";
import { api, unwrap, type SKU } from "@/api/client";
import { useApplyFieldErrors, useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { ExportButton } from "@/components/list/ExportButton";
import { useListPage } from "@/components/list/useListPage";
import { useI18n } from "@/i18n";
import { SkuThumb } from "@/components/SkuThumb";
import { planAllows } from "@/lib/plan";
import { useUrlFilter } from "@/lib/useUrlFilter";
import { SkuSelect } from "./mapping";

type Tracking = SKU["tracking"];
type Values = {
  code: string;
  name: string;
  barcode?: string | null;
  weightG?: number | null;
  brand?: string;
  lengthMm?: number | null;
  widthMm?: number | null;
  heightMm?: number | null;
  tracking: Tracking;
  kind: SKU["kind"];
  components?: { skuId?: string; quantity?: number }[];
};
type StatusFilter = "active" | "archived";

/** SKU (App Flow §7): the internal SKUs, the unit stock and orders are kept in. */
export function Catalog() {
  const { t, formatNumber } = useI18n();
  const navigate = useNavigate();
  const { me, can } = useAuth();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const errorText = useErrorText();
  const applyFieldErrors = useApplyFieldErrors();
  const [form] = Form.useForm<Values>();
  const kind = Form.useWatch("kind", form);
  // Filters live in the URL: kept per tab, across reloads, and shareable.
  const [search, setSearch] = useUrlFilter<string>("q", "");
  const [status, setStatus] = useUrlFilter<StatusFilter>("status", "active", ["active", "archived"]);
  const [kindFilter, setKindFilter] = useUrlFilter<"" | "single" | "bundle">("kind", "", ["", "single", "bundle"]);
  const [trackingFilter, setTrackingFilter] = useUrlFilter<"" | Tracking>("tracking", "", ["", "none", "lot", "serial"]);
  const [brand, setBrand] = useUrlFilter<string>("brand", "");
  const page = useListPage();
  const [editing, setEditing] = useState<SKU | "new" | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [safetyFor, setSafetyFor] = useState<string[] | null>(null);
  const safetyManage = can("inventory.manage") || can("channels.manage");
  const q = useDeferredValue(search.trim());
  const manage = can("catalog.manage");
  const lotSerial = planAllows(me?.workspace?.plan, "lot_serial");
  // Store listings still to map: the banner sends there.
  const summary = useQuery({
    queryKey: ["platform-skus", "summary"],
    queryFn: () => unwrap(api.GET("/v1/catalog/platform-skus", { params: { query: { limit: 1 } } })),
  });
  const unmapped = summary.data?.unmapped ?? 0;

  const filters = {
    status,
    ...(q ? { q } : {}),
    ...(kindFilter ? { kind: kindFilter } : {}),
    ...(trackingFilter ? { tracking: trackingFilter } : {}),
    ...(brand.trim() ? { brand: brand.trim() } : {}),
  };
  const list = useQuery({
    queryKey: ["skus", filters, page.query],
    queryFn: () => unwrap(api.GET("/v1/skus", { params: { query: { ...filters, ...page.query } } })),
    placeholderData: keepPreviousData,
  });
  const rows = list.data?.items ?? [];
  const activeTotal = list.data?.activeTotal ?? 0;

  const save = useMutation({
    mutationFn: (v: Values) => {
      const barcode = v.barcode?.trim() ? v.barcode.trim() : null;
      const extra = { weightG: v.weightG ?? null, brand: v.brand?.trim() ?? "", lengthMm: v.lengthMm ?? null, widthMm: v.widthMm ?? null, heightMm: v.heightMm ?? null };
      const bundle = v.kind === "bundle";
      const components = bundle ? (v.components ?? []).filter((c) => c.skuId).map((c) => ({ skuId: c.skuId!, quantity: c.quantity ?? 1 })) : undefined;
      const tracking = bundle ? "none" : v.tracking;
      if (editing === "new") return unwrap(api.POST("/v1/skus", { body: { code: v.code, name: v.name, barcode, tracking, kind: v.kind, components, ...extra } }));
      const s = editing as SKU;
      return unwrap(api.PATCH("/v1/skus/{skuID}", { params: { path: { skuID: s.id } }, body: { name: v.name, barcode, status: s.status, tracking, components, ...extra } }));
    },
    onSuccess: () => {
      setEditing(null);
      void queryClient.invalidateQueries({ queryKey: ["skus"] });
    },
    onError: (err) => {
      if (!applyFieldErrors(form, err)) void message.error(errorText(err));
    },
  });

  const setSkuStatus = (s: SKU, next: StatusFilter) =>
    unwrap(api.PATCH("/v1/skus/{skuID}", { params: { path: { skuID: s.id } }, body: { name: s.name, barcode: s.barcode, weightG: s.weightG, brand: s.brand, lengthMm: s.lengthMm, widthMm: s.widthMm, heightMm: s.heightMm, status: next } }))
      .then(() => queryClient.invalidateQueries({ queryKey: ["skus"] }))
      .catch((err: unknown) => void message.error(errorText(err)));

  const open = (target: SKU | "new") => {
    form.resetFields();
    form.setFieldsValue(
      target === "new" ? { tracking: "none", kind: "single" } : {
            kind: target.kind,
            components: target.components.map((c) => ({ skuId: c.skuId, quantity: c.quantity })),
            code: target.code,
            name: target.name,
            barcode: target.barcode,
            weightG: target.weightG,
            brand: target.brand,
            lengthMm: target.lengthMm,
            widthMm: target.widthMm,
            heightMm: target.heightMm,
            tracking: target.tracking,
          },
    );
    setEditing(target);
  };

  return (
    <>
      <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <Space align="baseline" size={16}>
          <Typography.Title level={2} style={{ margin: 0, fontSize: 28 }}>
            {t("catalog.title")}
          </Typography.Title>
          <Typography.Text type="secondary" className="z-num">
            {t("catalog.total", { n: activeTotal })}
          </Typography.Text>
        </Space>
        <Space wrap>
          <Button icon={<BarcodeOutlined />} onClick={() => navigate("/app/tools/barcodes?kind=sku")}>
            {t("bc.title")}
          </Button>
          {manage && (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => open("new")}>
              {t("catalog.add")}
            </Button>
          )}
        </Space>
      </Flex>
      {unmapped > 0 && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          title={t("catalog.unmappedBanner", { n: formatNumber(unmapped) })}
          description={t("catalog.unmappedBannerBody")}
          action={<Button onClick={() => navigate("/app/listings")}>{t("catalog.review")}</Button>}
        />
      )}

        <Flex gap={12} style={{ marginBottom: 16 }} justify="space-between" wrap>
          <Space wrap>
            <Input.Search allowClear placeholder={t("catalog.search")} value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: 320 }} />
            <Segmented<StatusFilter>
              value={status}
              onChange={setStatus}
              options={[
                { value: "active", label: t("catalog.active") },
                { value: "archived", label: t("catalog.archived") },
              ]}
            />
            <Select<"" | "single" | "bundle">
              value={kindFilter}
              onChange={setKindFilter}
              style={{ width: 150 }}
              options={[
                { value: "", label: t("catalog.allKinds") },
                { value: "single", label: t("catalog.kind.single") },
                { value: "bundle", label: t("catalog.bundle") },
              ]}
            />
            <Select<"" | Tracking>
              value={trackingFilter}
              onChange={setTrackingFilter}
              style={{ width: 170 }}
              options={[{ value: "", label: t("catalog.allTracking") }, ...(["none", "lot", "serial"] as const).map((v) => ({ value: v, label: t(`catalog.tracking.${v}`) }))]}
            />
            <Input allowClear placeholder={t("catalog.brand")} value={brand} onChange={(e) => setBrand(e.target.value)} style={{ width: 160 }} />
          </Space>
          <Space wrap>
            <ExportButton
              path="/v1/skus/export"
              query={filters}
              filename={t("catalog.title")}
              columns={[
                { key: "code", label: t("catalog.code") },
                { key: "name", label: t("common.name") },
                { key: "kind", label: t("catalog.kind"), values: { single: t("catalog.kind.single"), bundle: t("catalog.bundle") } },
                { key: "barcode", label: t("catalog.barcode") },
                { key: "brand", label: t("catalog.brand") },
                { key: "tracking", label: t("catalog.tracking"), values: Object.fromEntries((["none", "lot", "serial"] as const).map((v) => [v, t(`catalog.tracking.${v}`)])) },
                { key: "weightG", label: t("catalog.weight"), kind: "number" },
                { key: "lengthMm", label: t("catalog.lengthMm"), kind: "number" },
                { key: "widthMm", label: t("catalog.widthMm"), kind: "number" },
                { key: "heightMm", label: t("catalog.heightMm"), kind: "number" },
                { key: "safetyStock", label: t("catalog.safety"), kind: "number" },
                { key: "status", label: t("common.status"), values: { active: t("catalog.active"), archived: t("catalog.archived") } },
                { key: "updatedAt", label: t("catalog.updatedAt"), kind: "datetime" },
              ]}
            />
            {manage && safetyManage && (
              <Button disabled={selected.length === 0} onClick={() => setSafetyFor(selected)}>
                {t("catalog.safety.bulk", { n: selected.length })}
              </Button>
            )}
          </Space>
        </Flex>

        <Table<SKU>
          rowKey="id"
          loading={list.isFetching && !list.data}
          dataSource={rows}
          pagination={page.pagination(list.data)}
          locale={{ emptyText: t("catalog.empty") }}
          rowSelection={manage ? { selectedRowKeys: selected, onChange: (keys) => setSelected(keys as string[]) } : undefined}
          columns={[
            {
              title: t("catalog.code"),
              dataIndex: "code",
              width: 200,
              render: (v: string, s) => (
                <Space>
                  <SkuThumb skuId={s.id} size={32} />
                  <Typography.Link className="z-num" onClick={() => navigate(`/app/catalog/${s.id}`)}>
                    {v}
                  </Typography.Link>
                </Space>
              ),
            },
            {
              title: t("common.name"),
              dataIndex: "name",
              render: (v: string, s) =>
                s.kind === "bundle" ? (
                  <Space orientation="vertical" size={0}>
                    <Space size={6}>
                      {v}
                      <Tag color="purple">{t("catalog.bundle")}</Tag>
                    </Space>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {s.components.map((c) => `${c.quantity}× ${c.code}`).join(" + ")}
                    </Typography.Text>
                  </Space>
                ) : (
                  v
                ),
            },
            { title: t("catalog.brand"), dataIndex: "brand", width: 140, render: (v: string) => v || "—" },
            { title: t("catalog.barcode"), dataIndex: "barcode", width: 180, render: (v: string | null) => <span className="z-num">{v ?? "—"}</span> },
            { title: t("catalog.weight"), dataIndex: "weightG", width: 130, align: "right", render: (v: number | null) => <span className="z-num">{v ?? "—"}</span> },
            {
              title: t("catalog.tracking"),
              dataIndex: "tracking",
              width: 150,
              render: (v: Tracking) => (v === "none" ? "—" : <Tag color="processing">{t(`catalog.tracking.${v}`)}</Tag>),
            },
            {
              title: t("catalog.safety"),
              dataIndex: "safetyStock",
              width: 130,
              align: "right",
              render: (v: number | null) =>
                v === null ? <Typography.Text type="secondary">{t("catalog.safety.follow")}</Typography.Text> : <span className="z-num">{v}</span>,
            },
            {
              title: t("common.status"),
              dataIndex: "status",
              width: 120,
              render: (s: SKU["status"]) => (s === "active" ? <Tag color="success">{t("catalog.active")}</Tag> : <Tag>{t("catalog.archived")}</Tag>),
            },
            ...(manage
              ? [
                  {
                    title: t("common.actions"),
                    width: 220,
                    render: (_: unknown, s: SKU) => (
                      <Space>
                        <Button size="small" onClick={() => open(s)}>
                          {t("common.edit")}
                        </Button>
                        <Button size="small" onClick={() => void setSkuStatus(s, s.status === "active" ? "archived" : "active")}>
                          {s.status === "active" ? t("catalog.archive") : t("catalog.restore")}
                        </Button>
                      </Space>
                    ),
                  },
                ]
              : []),
          ]}
        />

      {safetyFor && (
        <SafetyModal
          skuIds={safetyFor}
          onClose={(saved) => {
            setSafetyFor(null);
            if (saved) setSelected([]);
          }}
        />
      )}
      <Modal
        open={editing !== null}
        title={editing === "new" ? t("catalog.add") : t("catalog.edit")}
        okText={t("common.save")}
        cancelText={t("common.cancel")}
        confirmLoading={save.isPending}
        onOk={() => form.submit()}
        onCancel={() => setEditing(null)}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" requiredMark={false} onFinish={(v) => save.mutate(v)}>
          <Form.Item
            name="code"
            label={t("catalog.code")}
            extra={editing === "new" ? t("catalog.codeHelp") : undefined}
            rules={[{ required: true, message: t("field.required") }, { pattern: /^\S{1,64}$/, message: t("field.invalid") }]}
          >
            <Input disabled={editing !== "new"} autoFocus={editing === "new"} maxLength={64} />
          </Form.Item>
          <Form.Item name="name" label={t("common.name")} rules={[{ required: true, whitespace: true, message: t("field.required") }, { max: 255, message: t("field.too_long") }]}>
            <Input autoFocus={editing !== "new"} />
          </Form.Item>
          <Flex gap={16}>
            <Form.Item name="barcode" label={`${t("catalog.barcode")} ${t("common.optional")}`} style={{ flex: 1 }} rules={[{ max: 64, message: t("field.too_long") }]}>
              <Input inputMode="numeric" />
            </Form.Item>
            <Form.Item name="weightG" label={`${t("catalog.weight")} ${t("common.optional")}`} style={{ width: 160 }}>
              <InputNumber min={0} max={1_000_000} style={{ width: "100%" }} />
            </Form.Item>
          </Flex>
          <Form.Item name="brand" label={`${t("catalog.brand")} ${t("common.optional")}`} rules={[{ max: 100, message: t("field.too_long") }]}>
            <Input maxLength={100} />
          </Form.Item>
          <Form.Item label={`${t("catalog.dimensions")} ${t("common.optional")}`} extra={t("catalog.dimensionsHelp")} style={{ marginBottom: 0 }}>
            <Flex gap={8}>
              {(["lengthMm", "widthMm", "heightMm"] as const).map((f) => (
                <Form.Item key={f} name={f} style={{ flex: 1 }}>
                  <InputNumber min={1} max={10_000} precision={0} placeholder={t(`catalog.${f}`)} style={{ width: "100%" }} />
                </Form.Item>
              ))}
            </Flex>
          </Form.Item>
          <Form.Item name="kind" label={t("catalog.kind")} extra={kind === "bundle" ? t("catalog.bundleHelp") : undefined}>
            <Radio.Group
              disabled={editing !== "new"}
              options={[
                { value: "single", label: t("catalog.kind.single") },
                { value: "bundle", label: t("catalog.bundle") },
              ]}
            />
          </Form.Item>
          {kind === "bundle" && (
            <Form.Item label={t("catalog.components")} required>
              <Form.List name="components">
                {(fields, { add, remove }) => (
                  <Space orientation="vertical" style={{ width: "100%" }}>
                    {fields.map((f) => (
                      <Flex key={f.key} gap={8} align="center">
                        <Form.Item name={[f.name, "skuId"]} noStyle rules={[{ required: true, message: t("field.required") }]}>
                          <SkuSelect />
                        </Form.Item>
                        <Form.Item name={[f.name, "quantity"]} noStyle initialValue={1}>
                          <InputNumber min={1} max={1000} precision={0} prefix="×" style={{ width: 110 }} />
                        </Form.Item>
                        <Button type="text" danger icon={<MinusCircleOutlined />} onClick={() => remove(f.name)} aria-label={t("common.delete")} />
                      </Flex>
                    ))}
                    <Button icon={<PlusOutlined />} onClick={() => add({ quantity: 1 })} disabled={fields.length >= 20}>
                      {t("catalog.addComponent")}
                    </Button>
                  </Space>
                )}
              </Form.List>
            </Form.Item>
          )}
          <Form.Item name="tracking" label={t("catalog.tracking")} extra={t("catalog.trackingHelp")} hidden={kind === "bundle"}>
            <Select<Tracking>
              options={(["none", "lot", "serial"] as const).map((v) => {
                // A plan without lot/serial keeps an SKU that already has it, but cannot pick it anew.
                const locked = v !== "none" && !lotSerial && !(editing !== "new" && editing?.tracking === v);
                return {
                  value: v,
                  disabled: locked,
                  label: (
                    <Flex justify="space-between" gap={8}>
                      <span>{t(`catalog.tracking.${v}`)}</span>
                      {locked && <Typography.Text type="secondary">{t("catalog.trackingPlan")}</Typography.Text>}
                    </Flex>
                  ),
                };
              })}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

/** Safety stock of the picked SKUs (Plan §6.3): their own number, or the warehouse's. */
function SafetyModal({ skuIds, onClose }: { skuIds: string[]; onClose: (saved: boolean) => void }) {
  const { t } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const queryClient = useQueryClient();
  const [own, setOwn] = useState(true);
  const [qty, setQty] = useState<number | null>(null);
  const save = useMutation({
    mutationFn: () => unwrap(api.PUT("/v1/stock-sync/safety", { body: { skuIds, safetyStock: own ? qty : null } })),
    onSuccess: ({ updated }) => {
      void message.success(t("catalog.safety.saved", { n: updated }));
      void queryClient.invalidateQueries({ queryKey: ["skus"] });
      onClose(true);
    },
    onError: (err) => void message.error(errorText(err)),
  });
  return (
    <Modal
      open
      title={t("catalog.safety.title", { n: skuIds.length })}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      okButtonProps={{ disabled: own && qty === null }}
      confirmLoading={save.isPending}
      onOk={() => save.mutate()}
      onCancel={() => onClose(false)}
      destroyOnHidden
    >
      <Space orientation="vertical" size={12} style={{ width: "100%" }}>
        <Typography.Text type="secondary">{t("catalog.safety.help")}</Typography.Text>
        <Radio.Group
          value={own}
          onChange={(e) => setOwn(e.target.value as boolean)}
          options={[
            { value: true, label: t("catalog.safety.own") },
            { value: false, label: t("catalog.safety.follow") },
          ]}
        />
        {own && <InputNumber autoFocus min={0} max={1_000_000} precision={0} suffix="pcs" value={qty} onChange={setQty} style={{ width: 160 }} />}
      </Space>
    </Modal>
  );
}
